/**
 * water_rainbow — the rainbow in a waterfall's mist (R14 of the 2026-09-25
 * water-realism wave). TSL port of the engine oracle
 * `waterLook.rainbowRgb` (the harness compares them sample for sample):
 *
 *   θ = the angle between the view ray and the ANTISOLAR point (−L);
 *   the primary bow 40.5–42.5° (violet inside, red outside), the secondary
 *   50–53° reversed at 0.4 — gated off while the sun stands above 42°.
 *
 * Drawn on a camera-facing quad through the mist: additive, soft against the
 * scene, an MRT overlay (light, not a surface).
 */
import * as THREE from 'three/webgpu';
import { Fn, acos, clamp, dot, float, length, normalize, positionWorld, cameraPosition, smoothstep, uniform, uv, vec3, vec4, max } from 'three/tsl';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';

type F = ReturnType<typeof float>;

/** One bow band (u = 0 inner edge … 1 outer), the CPU oracle's shape exactly. */
function band(t: F, lo: number, hi: number, reverse: boolean, gain: number): ReturnType<typeof vec3> {
  let u: F = t.sub(lo).div(hi - lo) as F;
  if (reverse) u = float(1).sub(u) as F;
  const edge = smoothstep(-0.25, 0.1, u).mul(float(1).sub(smoothstep(0.9, 1.25, u)));
  const inRange = smoothstep(lo - 0.61, lo - 0.6, t).mul(float(1).sub(smoothstep(hi + 0.6, hi + 0.61, t)));
  const r = smoothstep(0.45, 0.95, u);
  const g = max(float(1).sub(u.sub(0.55).abs().mul(2.4)), 0);
  const b = float(1).sub(smoothstep(0.05, 0.5, u));
  return vec3(r, g, b).mul(edge).mul(inRange).mul(gain) as unknown as ReturnType<typeof vec3>;
}

/** The bow colour for an angle from the antisolar point, in degrees. */
export function rainbowNode(thetaDeg: F): ReturnType<typeof vec3> {
  return band(thetaDeg, 40.5, 42.5, false, 1).add(band(thetaDeg, 50, 53, true, 0.4)) as unknown as ReturnType<typeof vec3>;
}

export interface RainbowHandles {
  material: THREE.MeshBasicNodeMaterial;
  /** World direction TO the sun (the sky's uSunDir). */
  uSunDir: { value: THREE.Vector3 };
  /** Overall strength (the sun gate × illuminance × mist density). */
  uStrength: { value: number };
}

/** The rainbow billboard's material (one per session — every fall's quad shares it). */
export function createRainbowMaterial(): RainbowHandles {
  const uSun = uniform(new THREE.Vector3(0.3, 0.5, 0.2));
  const uStrength = uniform(0);
  const m = new THREE.MeshBasicNodeMaterial();
  m.transparent = true;
  m.depthWrite = false;
  m.blending = THREE.AdditiveBlending;
  m.side = THREE.DoubleSide;
  m.fog = false;
  m.colorNode = Fn(() => {
    const vdir = normalize(positionWorld.sub(cameraPosition));
    const anti = normalize(uSun).negate();
    const theta = acos(clamp(dot(vdir, anti), -1, 1)).mul(180 / Math.PI);
    const col = rainbowNode(theta as F);
    // The mist is densest in the middle of the quad: a soft disc.
    const r = length(uv().sub(0.5)).mul(2);
    const fade = float(1).sub(smoothstep(0.35, 1.0, r));
    return vec4(col.mul(1.4), fade.mul(uStrength));
  })();
  markMrtOverlay(m);
  return { material: m, uSunDir: uSun as unknown as { value: THREE.Vector3 }, uStrength: uStrength as unknown as { value: number } };
}
